import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'

import { landingLeadReadPermissions, permissions, routeReadPermission } from '@/auth/permissions'
import { portalBaseUrl, portalEntryUrl } from '@/config/portal'
import { useAuthStore } from '@/stores/auth'
import { inboxQueryValue } from '@/utils/inboxLocation'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    children: [
      {
        path: 'leads',
        name: 'landing-leads',
        component: () => import('@/views/crm/LandingLeadsView.vue'),
        meta: { title: 'Заявки с сайта', requiredPermissions: landingLeadReadPermissions },
      },
      {
        // Keep the normal backoffice shell, but no public navigation/permission entry.
        // The API authorizes the account before the view shows its title or data.
        path: 'private/finance',
        name: 'private-finance',
        component: () => import('@/views/private/PrivateFinanceView.vue'),
      },
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: 'Рабочий стол' },
      },
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/auth/ForbiddenView.vue'),
        meta: { title: 'Недостаточно прав' },
      },
      {
        path: 'students',
        name: 'students',
        component: () => import('@/views/students/StudentsView.vue'),
        meta: { title: 'Ученики' },
      },
      {
        path: 'students/new',
        name: 'student-new',
        component: () => import('@/views/students/StudentDetailsView.vue'),
        meta: { title: 'Новый ученик', permission: permissions.studentsWrite },
      },
      {
        path: 'students/:id',
        name: 'student-details',
        component: () => import('@/views/students/StudentDetailsView.vue'),
        meta: { title: 'Карточка ученика' },
      },
      {
        path: 'teachers',
        name: 'teachers',
        component: () => import('@/views/staff/TeachersView.vue'),
        meta: { title: 'Преподаватели', wide: true },
      },
      {
        path: 'teachers/new',
        name: 'teacher-new',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: {
          title: 'Новый преподаватель',
          resource: 'staff',
          returnPath: '/teachers',
          permission: permissions.staffWrite,
          create: true,
          staffPosition: 'teacher',
        },
      },
      {
        path: 'teachers/:id',
        name: 'teacher-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: {
          title: 'Карточка преподавателя',
          resource: 'staff',
          returnPath: '/teachers',
        },
      },
      {
        path: 'staff',
        name: 'staff',
        component: () => import('@/views/resources/ResourceListRoute.vue'),
        meta: { title: 'Сотрудники', resource: 'staff' },
      },
      {
        path: 'staff/new',
        name: 'staff-new',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: {
          title: 'Новый сотрудник',
          resource: 'staff',
          returnPath: '/staff',
          permission: permissions.staffWrite,
          create: true,
          staffPosition: 'staff',
        },
      },
      {
        path: 'staff/:id',
        name: 'staff-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: { title: 'Карточка сотрудника', resource: 'staff' },
      },
      {
        path: 'catalog/programs',
        name: 'programs',
        component: () => import('@/views/learning/AcademicListView.vue'),
        meta: { title: 'Программы', resource: 'programs' },
      },
      {
        path: 'catalog/programs/:id',
        name: 'program-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: { title: 'Карточка программы', resource: 'programs' },
      },
      {
        path: 'catalog/courses',
        name: 'courses',
        component: () => import('@/views/learning/AcademicListView.vue'),
        meta: { title: 'Курсы', resource: 'courses' },
      },
      {
        path: 'catalog/courses/:id',
        name: 'course-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: { title: 'Карточка курса', resource: 'courses' },
      },
      {
        path: 'registrations',
        name: 'registrations',
        component: () => import('@/views/learning/AcademicListView.vue'),
        meta: { title: 'Записи на курсы', resource: 'registrations' },
      },
      {
        path: 'registrations/:id',
        name: 'registration-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: { title: 'Карточка записи', resource: 'registrations' },
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import('@/views/scheduling/CalendarView.vue'),
        meta: { title: 'Календарь уроков', wide: true },
      },
      {
        path: 'lessons',
        name: 'lessons',
        component: () => import('@/views/scheduling/LessonsListView.vue'),
        meta: { title: 'Уроки', wide: true },
      },
      {
        path: 'lessons/:id',
        name: 'lesson-details',
        component: () => import('@/views/scheduling/LessonDetailsView.vue'),
        meta: { title: 'Карточка урока', wide: true },
      },
      {
        path: 'classroom/test',
        name: 'classroom-test',
        component: () => import('@/views/classroom/ClassroomTestView.vue'),
        meta: { title: 'Тестовый онлайн-класс', permission: permissions.schedulingWrite },
      },
      {
        path: 'classroom/recordings',
        name: 'classroom-recordings',
        component: () => import('@/views/classroom/ClassroomRecordingsView.vue'),
        meta: { title: 'Записи онлайн-уроков', permission: permissions.classroomRecordingsRead, wide: true },
      },
      {
        path: 'classroom/recordings/:lessonId',
        name: 'classroom-recording-details',
        component: () => import('@/views/classroom/ClassroomRecordingDetailsView.vue'),
        meta: { title: 'Записи урока', permission: permissions.classroomRecordingsRead, wide: true },
      },
      {
        path: 'lesson-schedules',
        name: 'lesson-schedules',
        component: () => import('@/views/learning/AcademicListView.vue'),
        meta: { title: 'Правила расписания', resource: 'lessonSchedules' },
      },
      {
        path: 'lesson-schedules/:id',
        name: 'lesson-schedule-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: { title: 'Карточка серии уроков', resource: 'lessonSchedules' },
      },
      {
        path: 'learning/assignments',
        name: 'assignments',
        component: () => import('@/views/learning/LearningListView.vue'),
        meta: { title: 'Задания', resource: 'assignments' },
      },
      {
        path: 'learning/assignments/:id',
        name: 'assignment-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: { title: 'Карточка задания', resource: 'assignments' },
      },
      {
        path: 'learning/submissions',
        name: 'submissions',
        component: () => import('@/views/learning/LearningListView.vue'),
        meta: { title: 'Ответы учеников', resource: 'submissions' },
      },
      {
        path: 'learning/submissions/:id',
        name: 'submission-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: { title: 'Ответ ученика', resource: 'submissions' },
      },
      {
        path: 'student-reviews/moderation',
        name: 'review-moderation',
        redirect: to => ({ name: 'student-reviews', query: to.query, hash: to.hash }),
      },
      {
        path: 'student-reviews',
        name: 'student-reviews',
        component: () => import('@/views/reviews/StudentReviewsView.vue'),
        meta: { title: 'Отзывы', permission: permissions.reviewsRead, wide: true },
      },
      {
        path: 'settings/reviews',
        name: 'review-settings',
        component: () => import('@/views/settings/ReviewSettingsView.vue'),
        meta: { title: 'Настройки отзывов', permission: permissions.settingsRead, wide: true },
      },
      {
        path: 'review-forms',
        name: 'review-form-templates',
        component: () => import('@/views/reviews/ReviewFormTemplatesView.vue'),
        meta: { title: 'Шаблоны анкет', permission: permissions.reviewsRead, wide: true },
      },
      {
        path: 'review-form-responses',
        name: 'review-form-responses',
        component: () => import('@/views/reviews/ReviewFormResponsesView.vue'),
        meta: { title: 'Анкеты по занятиям', permission: permissions.reviewsRead, wide: true },
      },
      {
        path: 'subscriptions/categories',
        name: 'legacy-subscription-categories',
        redirect: { name: 'subscription-catalog' },
      },
      {
        path: 'subscriptions/categories/:id',
        name: 'subscription-category-details',
        redirect: to => ({ name: 'subscription-details', params: { id: to.params.id } }),
      },
      {
        path: 'subscriptions/plans',
        name: 'legacy-subscription-plans',
        redirect: { name: 'subscription-catalog' },
      },
      {
        path: 'subscriptions/plans/:id',
        name: 'subscription-plan-details',
        redirect: { name: 'subscription-catalog' },
      },
      {
        path: 'subscriptions',
        name: 'subscription-catalog',
        component: () => import('@/views/subscriptions/SubscriptionCatalogView.vue'),
        meta: { title: 'Абонементы' },
      },
      {
        path: 'subscriptions/packages/:id',
        name: 'subscription-package-details',
        redirect: { name: 'subscription-catalog' },
      },
      {
        path: 'subscriptions/new',
        name: 'subscription-new',
        component: () => import('@/views/subscriptions/SubscriptionEditorView.vue'),
        meta: { title: 'Новый абонемент', permission: permissions.subscriptionsWrite, wide: true },
      },
      {
        path: 'subscriptions/sold',
        name: 'student-subscriptions',
        component: () => import('@/views/subscriptions/StudentSubscriptionsView.vue'),
        meta: { title: 'Проданные абонементы' },
      },
      {
        path: 'subscriptions/payment-required',
        name: 'payment-requirements',
        component: () => import('@/views/subscriptions/PaymentRequirementsView.vue'),
        meta: { title: 'Требуют оплаты', permission: permissions.subscriptionsRead, wide: true },
      },
      {
        path: 'subscriptions/sold/:id',
        name: 'student-subscription-details',
        component: () => import('@/views/subscriptions/StudentSubscriptionDetailsView.vue'),
        meta: { title: 'Абонемент ученика', wide: true },
      },
      {
        path: 'subscriptions/:id',
        name: 'subscription-details',
        component: () => import('@/views/subscriptions/SubscriptionEditorView.vue'),
        meta: { title: 'Абонемент и пакеты', permission: permissions.subscriptionsRead, wide: true },
      },
      {
        path: 'billing/accounts',
        name: 'billing-accounts',
        component: () => import('@/views/billing/BillingAccountsView.vue'),
        meta: { title: 'Лицевые счета' },
      },
      {
        path: 'billing/accounts/:id',
        name: 'billing-account-details',
        component: () => import('@/views/billing/BillingAccountDetailsView.vue'),
        meta: { title: 'Лицевой счёт' },
      },
      {
        path: 'billing/invoices',
        name: 'invoices',
        component: () => import('@/views/billing/InvoicesView.vue'),
        meta: { title: 'Счета' },
      },
      {
        path: 'billing/invoices/:id',
        name: 'invoice-details',
        component: () => import('@/views/billing/InvoiceDetailsView.vue'),
        meta: { title: 'Счёт на оплату' },
      },
      {
        path: 'billing/payments',
        name: 'payments',
        component: () => import('@/views/billing/PaymentsView.vue'),
        meta: { title: 'Платежи' },
      },
      {
        path: 'billing/payments/:id',
        name: 'payment-details',
        component: () => import('@/views/billing/PaymentDetailsView.vue'),
        meta: { title: 'Платёж' },
      },
      {
        path: 'payroll',
        name: 'payroll',
        component: () => import('@/views/payroll/PayrollView.vue'),
        meta: { title: 'Зарплата преподавателей', permission: permissions.payrollRead, wide: true },
      },
      {
        path: 'payroll/rates',
        name: 'payroll-rates',
        component: () => import('@/views/payroll/PayrollRatesView.vue'),
        meta: { title: 'Ставки преподавателей', permission: permissions.payrollRead, wide: true },
      },
      {
        path: 'payroll/periods/:id',
        name: 'payroll-period-details',
        component: () => import('@/views/payroll/PayrollPeriodDetailsView.vue'),
        meta: { title: 'Расчётная ведомость', permission: permissions.payrollRead, wide: true },
      },
      {
        path: 'payroll/periods/:periodId/staff/:staffId',
        name: 'payroll-staff-details',
        component: () => import('@/views/payroll/PayrollStaffDetailsView.vue'),
        meta: { title: 'Расчёт преподавателя', permission: permissions.payrollRead, wide: true },
      },
      {
        path: 'crm/notes',
        name: 'activity-notes',
        component: () => import('@/views/crm/ActivityNotesView.vue'),
        meta: { title: 'Заметки CRM' },
      },
      {
        path: 'crm/notes/:id',
        name: 'activity-note-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: { title: 'Заметка CRM', resource: 'activityNotes' },
      },
      {
        path: 'crm/tasks',
        name: 'crm-tasks',
        component: () => import('@/views/crm/CrmTasksView.vue'),
        meta: { title: 'Задачи', permission: permissions.crmRead, wide: true },
      },
      {
        path: 'crm/tasks/:id',
        name: 'crm-task-details',
        redirect: to => ({ name: 'crm-tasks', query: { ...to.query, task: String(to.params.id) } }),
      },
      {
        path: 'crm/task-categories',
        name: 'crm-task-categories',
        component: () => import('@/views/crm/CrmTaskTypesView.vue'),
        meta: { title: 'Типы задач', permission: permissions.crmRead },
      },
      {
        path: 'crm/task-categories/:id',
        name: 'crm-task-category-details',
        redirect: to => ({ name: 'crm-task-categories', query: { ...to.query, type: String(to.params.id) } }),
      },
      {
        path: 'communications/client-work',
        name: 'client-work',
        redirect: to => ({ name: 'crm-tasks', query: typeof to.query.studentId === 'string' ? { studentId: to.query.studentId } : {} }),
      },
      {
        path: 'communications/campaigns',
        name: 'messenger-campaigns',
        component: () => import('@/views/communications/MessengerCampaignsView.vue'),
        meta: { title: 'Рассылки', permission: permissions.campaignsRead, wide: true },
      },
      {
        path: 'communications/campaigns/:campaignId/history/:runId?',
        name: 'messenger-campaign-history',
        component: () => import('@/views/communications/CampaignHistoryView.vue'),
        meta: { title: 'История отправок', permission: permissions.campaignsRead, wide: true },
      },
      {
        path: 'communications/portal',
        name: 'portal-support-inbox',
        redirect: to => {
          const supportId = inboxQueryValue(to.query, 'supportId')
            ?? inboxQueryValue(to.query, 'conversationId') ?? inboxQueryValue(to.query, 'conversation')
          const { conversationId, conversation, ...query } = to.query
          return { name: 'unified-student-inbox', query: { ...query, channel: 'support', supportId: supportId || undefined }, hash: to.hash }
        },
        meta: { title: 'Входящие', permission: permissions.messagesRead, wide: true },
      },
      {
        path: 'communications/inbox',
        name: 'unified-student-inbox',
        component: () => import('@/views/communications/UnifiedStudentInboxView.vue'),
        meta: { title: 'Входящие', permission: permissions.messagesRead, wide: true },
      },
      {
        path: 'communications/teaching-chats',
        name: 'teaching-chats',
        component: () => import('@/views/communications/TeachingChatsView.vue'),
        meta: { title: 'Учебные диалоги', permission: permissions.teachingChatsRead, wide: true },
      },
      {
        path: 'communications/:channel(telegram|vk|avito|max)',
        name: 'channel-inbox',
        component: () => import('@/views/communications/ChannelInboxView.vue'),
        meta: { title: 'Коммуникации', permission: permissions.messagesRead, wide: true },
      },
      {
        path: 'telegram',
        redirect: '/communications/telegram',
      },
      {
        path: 'vk',
        redirect: '/communications/vk',
      },
      {
        path: 'avito',
        redirect: '/communications/avito',
      },
      {
        path: 'messages',
        name: 'messages',
        component: () => import('@/views/resources/ResourceListRoute.vue'),
        meta: { title: 'Сообщения', resource: 'messages' },
      },
      {
        path: 'messages/:id',
        name: 'message-details',
        component: () => import('@/views/resources/ResourceDetailsView.vue'),
        meta: { title: 'Сообщение', resource: 'messages' },
      },
      {
        path: 'settings/organization',
        name: 'organization',
        component: () => import('@/views/settings/OrganizationView.vue'),
        meta: { title: 'Организация' },
      },
      {
        path: 'settings/accounts',
        name: 'accounts',
        component: () => import('@/views/settings/AccountsView.vue'),
        meta: { title: 'Пользователи и права', permission: permissions.accountsManage },
      },
      {
        path: 'settings/menu',
        name: 'navigation-settings',
        component: () => import('@/views/settings/NavigationSettingsView.vue'),
        meta: { title: 'Меню' },
      },
      {
        path: 'settings/student-statuses',
        name: 'student-statuses',
        component: () => import('@/views/settings/StudentStatusesView.vue'),
        meta: { title: 'Статусы учеников', permission: permissions.settingsRead },
      },
      {
        path: 'settings/subjects',
        name: 'subjects',
        component: () => import('@/views/settings/SubjectsView.vue'),
        meta: { title: 'Предметы' },
      },
      {
        path: 'settings/locations',
        name: 'locations',
        component: () => import('@/views/settings/LocationsView.vue'),
        meta: { title: 'Филиалы' },
      },
      {
        path: 'settings/rooms',
        name: 'rooms',
        component: () => import('@/views/settings/RoomsView.vue'),
        meta: { title: 'Помещения' },
      },
      {
        path: 'settings/data-model',
        name: 'data-model',
        component: () => import('@/views/settings/DataModelView.vue'),
        meta: { title: 'Модель данных' },
      },
    ],
  },
  {
    path: '/classroom/replay/:lessonId/:recordingId',
    name: 'classroom-recording-player',
    component: () => import('@/views/classroom/ClassroomReplayView.vue'),
    meta: { title: 'Просмотр записи урока', permission: permissions.classroomRecordingsRead },
  },
  {
    path: '/ui-kit',
    name: 'ui-kit',
    component: () => import('@/views/ui-kit/UiKitView.vue'),
    props: { context: 'app' },
    meta: { title: 'Interface system', public: true },
  },
  {
    path: '/pay/:token',
    name: 'public-subscription-payment',
    component: () => import('@/views/subscriptions/PublicSubscriptionPaymentView.vue'),
    meta: { title: 'Оплата абонемента', public: true },
  },
  {
    path: '/review/:token',
    name: 'public-student-review',
    component: () => import('@/views/reviews/PublicStudentReviewView.vue'),
    meta: { title: 'Отзыв об ученике', public: true },
  },
  {
    path: '/forms/:token',
    alias: '/form/:token',
    name: 'public-review-form',
    component: () => import('@/views/reviews/PublicReviewFormView.vue'),
    meta: { title: 'Анкета обратной связи', public: true },
  },
  {
    path: '/join/:code',
    name: 'public-student-classroom',
    component: () => import('@/views/classroom/PublicStudentClassroomView.vue'),
    meta: { title: 'Мои уроки', public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { title: 'Вход', public: true },
  },
  {
    path: '/:prefix/:initialCode([a-z]+-[a-z]+-[0-9]{4})',
    alias: '/go/:prefix/:initialCode',
    name: 'short-link-prefixed',
    component: () => import('@/views/ShortLinkEntryView.vue'),
    props: true,
    meta: { title: 'Открыть ссылку', public: true, skipAuthInitialization: true },
  },
  {
    path: '/open/:initialCode?',
    alias: '/go/:initialCode?',
    name: 'short-link-entry',
    component: () => import('@/views/ShortLinkEntryView.vue'),
    props: true,
    meta: { title: 'Открыть ссылку', public: true, skipAuthInitialization: true },
  },
  {
    path: '/:initialCode([a-z]+-[a-z]+-[0-9]{4})',
    name: 'short-link-code',
    component: () => import('@/views/ShortLinkEntryView.vue'),
    props: true,
    meta: { title: 'Открыть ссылку', public: true, skipAuthInitialization: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Страница не найдена' },
  },
]

export function backofficeScrollBehavior(
  to: Pick<RouteLocationNormalized, 'name'>,
  from: Pick<RouteLocationNormalized, 'name'>,
  savedPosition: { left: number; top: number } | null = null,
): { left?: number; top: number } | false {
  if (savedPosition) return savedPosition
  // Schedule controls persist their state in the query string. This remains
  // the same screen, so a date, time or mode change must not reset scroll.
  if (to.name === 'teachers' && from.name === 'teachers') return false
  return { top: 0 }
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: backofficeScrollBehavior,
})

export function routeNeedsAuthInitialization(route: {
  name?: unknown
  meta?: { public?: boolean }
}): boolean {
  return route.meta?.public !== true || route.name === 'login'
}

function portalOnly(session: { permissions: string[] } | null): boolean {
  if (!session?.permissions.length) return false
  return session.permissions.every(permission => permission.startsWith('portal.'))
}

router.beforeEach(async to => {
  const auth = useAuthStore()

  // Public one-time forms must not probe the private browser session. Besides
  // avoiding an unnecessary request, this prevents an auth failure from
  // replacing the public form with the login route.
  if (!routeNeedsAuthInitialization(to)) return true

  await auth.initialize()

  if (to.meta.public) {
    if (to.name === 'login' && auth.authenticated) {
      const redirect = typeof to.query.redirect === 'string'
        && to.query.redirect.startsWith('/')
        && !to.query.redirect.startsWith('//')
        ? to.query.redirect
        : '/'
      if (redirect === '/portal' || redirect.startsWith('/portal/')) {
        const portalPath = redirect.slice('/portal'.length) || '/'
        window.location.replace(portalEntryUrl(portalPath))
        return false
      }
      return redirect
    }
    return true
  }

  if (!auth.authenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (portalOnly(auth.session) && to.name !== 'forbidden') {
    window.location.replace(portalBaseUrl())
    return false
  }

  const required = to.meta.permission ?? routeReadPermission(to.path)
  if (required && !auth.hasPermission(required)) return { name: 'forbidden' }
  if (to.meta.requiredPermissions?.some(permission => !auth.hasPermission(permission))) return { name: 'forbidden' }
  return true
})

if (typeof window !== 'undefined') {
  window.addEventListener('avoschool:auth-required', () => {
    const auth = useAuthStore()
    auth.clear()
    const current = router.currentRoute.value
    if (current.name !== 'login') {
      void router.replace({ name: 'login', query: { redirect: current.fullPath } })
    }
  })
}

router.afterEach(to => {
  document.title = to.meta.title ? `${String(to.meta.title)} · Avoschool` : 'Avoschool'
})

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    resource?: string
    returnPath?: string
    wide?: boolean
    public?: boolean
    permission?: string
    requiredPermissions?: readonly string[]
    create?: boolean
    staffPosition?: 'staff' | 'teacher'
  }
}
